import type { Scene } from '@babylonjs/core/scene';
import {
  Action, TICK_RATE, aimTarget, canBeHit, canCounter, areAllies, launchProjectile,
  stepProjectile, projectileSweepHits,
  type ArenaDefinition, type AttackDef, type Placement, type PlayerState, type ProjectileSim,
} from '@arena/shared';
import { kitOf } from '@arena/character-data';
import { BulletView } from './bulletView.ts';

export interface Target {state:PlayerState;position:Placement;isLocal:boolean}
interface Shot {sim:ProjectileSim;view:BulletView;accumulator:number}

/** Immediate local pistol feedback. Only the server applies hits and damage. */
export class PistolVisuals {
  private readonly scene:Scene;
  private readonly shots:Shot[]=[];
  private readonly seen=new Map<number,number>();
  private time=0;
  private lastSerial:number|null=null;
  constructor(scene:Scene){this.scene=scene;}

  update(local:PlayerState|null,characterId:string,team:number,arena:ArenaDefinition,targets:readonly Target[],dt:number):void {
    this.time+=dt;
    if(local){
      const serial=local.actionSerial;
      if(this.lastSerial!==null && (serial-this.lastSerial+65536)%65536>32768){
        // A rejected predicted action may reuse its serial on the next real shot.
        // Rewinding inside the same action keeps the serial and stays deduplicated.
        for(const seen of this.seen.keys())if((seen-serial+65536)%65536<32768 && seen!==serial)this.seen.delete(seen);
      }
      this.lastSerial=serial;
    }
    for(const [serial,at]of this.seen)if(this.time-at>3)this.seen.delete(serial);
    for(let i=this.shots.length-1;i>=0;i--){
      const shot=this.shots[i]!;
      shot.accumulator+=Math.min(dt,0.2);
      let alive=true;
      while(shot.accumulator>=1/TICK_RATE && alive){
        shot.accumulator-=1/TICK_RATE;
        const fromX=shot.sim.x,fromZ=shot.sim.z;
        alive=stepProjectile(shot.sim,arena,1/TICK_RATE);
        if(targets.some(t=>!t.isLocal && !t.state.knockedOut && t.state.spawnShieldTicks===0 &&
          !areAllies(team,t.state.team) &&
          (canBeHit(t.state,kitOf(t.state.characterId).table,shot.sim.def) || canCounter(t.state,kitOf(t.state.characterId).table)) &&
          projectileSweepHits(shot.sim,fromX,fromZ,t.position)))alive=false;
      }
      if(!alive){shot.view.dispose();this.shots.splice(i,1);continue;}
      // Draw smoothly between the fixed simulation steps, without moving hitboxes.
      const ahead=shot.accumulator;
      shot.view.update(shot.sim.x+shot.sim.vx*ahead,shot.sim.y+shot.sim.vy*ahead,shot.sim.z+shot.sim.vz*ahead,shot.sim.vx,shot.sim.vz,dt);
    }
    if(!local || characterId!=='mussolini' || local.knockedOut || local.matchFrozen || local.hitstun>0)return;
    if(local.action!==Action.Light3 && local.action!==Action.AirAttack)return;
    const def=kitOf(characterId).table[local.action]!;
    if(local.actionTick<def.startup || local.actionTick>=def.startup+def.active+3 || this.seen.has(local.actionSerial))return;
    this.seen.set(local.actionSerial,this.time);
    // The same lock-on as the server's, on the enemies as drawn here.
    const sim=launchProjectile(local,def,aimTarget(local,def.projectile!,enemies(targets,team,def))),view=new BulletView(this.scene);
    const catchUp=local.actionTick-def.startup;
    for(let i=0;i<catchUp;i++)if(!stepProjectile(sim,arena,1/TICK_RATE)){view.dispose();return;}
    view.update(sim.x,sim.y,sim.z,sim.vx,sim.vz,0);
    this.shots.push({sim,view,accumulator:0});
  }

  clear():void {for(const s of this.shots)s.view.dispose();this.shots.length=0;this.seen.clear();this.lastSerial=null;}
}

/** Where the enemies an aim-assisted shot can lock onto stand, as drawn on this client. */
export function enemies(targets:readonly Target[],team:number,def:AttackDef):Placement[]{
  return targets.filter(t=>!t.isLocal && !t.state.knockedOut && t.state.spawnShieldTicks===0 &&
    !areAllies(team,t.state.team) && canBeHit(t.state,kitOf(t.state.characterId).table,def)).map(t=>t.position);
}

/** The owner's shot is already drawn immediately; don't redraw its delayed echo. */
export class HiddenPistolEcho {
  update(_x:number,_y:number,_z:number,_fx:number,_fz:number,_dt:number):void{}
  dispose():void{}
}
